import {
    adminControlResultURL,
    adminControlURL,
    getTeamsAsyncResult,
    getTeamsBatchAsyncResult,
    getTenantCompliancePoliciesURL,
    getTenantURL,
    getTenantUsersURL,
    getUserCompliancePolicyFromDeviceURL,
    getUserCompliancePolicyURL,
    getVoiceAppsURL,
    queryPresenceStatusURL
} from '../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { AdminAction } from '../../../ReactRouting/Components/Organisms/AdminControl/Types'
import { requestJSONAsync } from './AsyncFetching'
import { ActionIDs } from '../../../ReactRouting/Components/Organisms/AdminControl/Actions'
import {
    PolicyAssignment,
    PresenceStatus,
    TenantCompliancePolicy,
    UserCompliancePoliciesWrapper,
    UserCompliancePolicy,
    UsersCompliancePoliciesWrapper
} from '../Types/Types'
import { TimingOptions } from '../../../NonReact/common/TimingOptions'

export type AdminControlRequest = {
    action: AdminAction
} & {
    [k in ActionIDs]?: string
}

export type AdminControlResponse = {
    message: string
}

const TIMING_OPTIONS: TimingOptions = new TimingOptions({
    maxChecks: 100,
    frequency: 1000
})

/**
 * Sends an asynchronous admin control request to the specified telsys and partition.
 *
 * @param telsysID - The ID of the telsys.
 * @param partitionID - The ID of the partition.
 * @param request - The admin control request object containing the action and any additional parameters.
 */
export const adminControlRequestAsync = (
    telsysID: string | number,
    partitionID: string | number,
    request: AdminControlRequest
): Promise<Nullable<AdminControlResponse>> =>
    requestJSONAsync<AdminControlResponse>(
        adminControlURL(telsysID, partitionID),
        adminControlResultURL(telsysID, partitionID),
        {
            method: RESTMethods.PUT,
            body: request,
            timingOptions: TIMING_OPTIONS
        }
    )

/**
 * Fetches the tenant compliance policies asynchronously for the specified telsys.
 *
 * @param telsys - The ID of the telsys.
 */
export const getTenantCompliancePoliciesAsync = (
    telsys: string | number
): Promise<Nullable<TenantCompliancePolicy[]>> =>
    requestJSONAsync<TenantCompliancePolicy[]>(getTenantCompliancePoliciesURL(telsys), getTeamsAsyncResult(telsys), {
        method: RESTMethods.GET,
        timingOptions: TIMING_OPTIONS
    })

/**
 * Fetches the user compliance policy asynchronously for a specific device.
 *
 * @param telsys - The ID of the telsys.
 * @param partition - The ID of the partition.
 * @param idOnTelSys - The ID of the device on the telsys.
 */
export const getUserCompliancePolicyByDeviceAsync = (
    telsys: string | number,
    partition: string | number,
    idOnTelSys: string
): Promise<Nullable<UserCompliancePolicy[]>> =>
    requestJSONAsync<UserCompliancePolicy[]>(
        getUserCompliancePolicyFromDeviceURL(telsys, partition, idOnTelSys),
        getTeamsAsyncResult(telsys),
        {
            method: RESTMethods.GET,
            timingOptions: TIMING_OPTIONS
        }
    )

/**
 * Fetches the user compliance policy along with tenant compliance policies asynchronously.
 *
 * @param telsys - The ID of the telsys.
 * @param partition - The ID of the partition.
 * @param idOnTelSys - The ID of the device on the telsys.
 */
export const getUserCompliancePolicyByUserAsync = (
    telsys: string | number,
    partition: string | number,
    idOnTelSys: string
): Promise<Nullable<UserCompliancePoliciesWrapper>> =>
    Promise.all([
        getUserCompliancePolicyByDeviceAsync(telsys, partition, idOnTelSys),
        getTenantCompliancePoliciesAsync(telsys)
    ]).then(
        ([userCompliancePolicies, tenantCompliancePolicies]: [
            Nullable<UserCompliancePolicy[]>,
            Nullable<TenantCompliancePolicy[]>
        ]): Nullable<UserCompliancePoliciesWrapper> => ({
            userCompliancePolicies,
            tenantCompliancePolicies
        })
    )

/**
 * Fetches the users' compliance policies asynchronously for the specified telsys and partition.
 *
 * @param telsys - The ID of the telsys.
 * @param partition - The ID of the partition.
 */
export const getUsersCompliancePolicyAsync = (
    telsys: string | number,
    partition: string | number
): Promise<Nullable<Record<string, UserCompliancePolicy[]>>> =>
    requestJSONAsync<Record<string, UserCompliancePolicy[]>>(
        getUserCompliancePolicyURL(telsys, partition),
        getTeamsBatchAsyncResult(telsys),
        {
            method: RESTMethods.GET,
            timingOptions: TIMING_OPTIONS
        }
    )

/**
 * Fetches all users' compliance policies along with tenant compliance policies asynchronously.
 *
 * @param telsys - The ID of the telsys.
 * @param partition - The ID of the partition.
 */
export const getAllUsersCompliancePolicyAsync = (
    telsys: string | number,
    partition: string | number
): Promise<Nullable<UsersCompliancePoliciesWrapper>> =>
    Promise.all([getUsersCompliancePolicyAsync(telsys, partition), getTenantCompliancePoliciesAsync(telsys)]).then(
        ([usersCompliancePolicies, tenantCompliancePolicies]: [
            Nullable<Record<string, UserCompliancePolicy[]>>,
            Nullable<TenantCompliancePolicy[]>
        ]): UsersCompliancePoliciesWrapper => ({
            usersCompliancePolicies,
            tenantCompliancePolicies
        })
    )

/**
 * Queries the presence status asynchronously for a specific user.
 *
 * @param telsys - The ID of the telsys.
 * @param partition - The ID of the partition.
 * @param idOnTelSys - The ID of the user on the telsys.
 */
export const queryPresenceStatusAsync = (
    telsys: string | number,
    partition: string | number,
    idOnTelSys: string
): Promise<Nullable<PresenceStatus>> =>
    requestJSONAsync<PresenceStatus>(
        queryPresenceStatusURL(telsys, partition, idOnTelSys),
        getTeamsAsyncResult(telsys),
        {
            method: RESTMethods.GET,
            timingOptions: TIMING_OPTIONS
        }
    )

/**
 * Applies a compliance policy asynchronously to a specific user.
 *
 * @param telsys - The ID of the telsys.
 * @param partition - The ID of the partition.
 * @param idOnTelSys - The ID of the user on the telsys.
 * @param policy - The policy assignment to be applied.
 */
export const applyCompliancePolicy = (
    telsys: string | number,
    partition: string | number,
    idOnTelSys: string,
    policy: PolicyAssignment[]
): Promise<Nullable<void>> =>
    requestJSONAsync<void>(
        getUserCompliancePolicyFromDeviceURL(telsys, partition, idOnTelSys),
        getTeamsAsyncResult(telsys),
        {
            method: RESTMethods.PUT,
            body: policy,
            timingOptions: TIMING_OPTIONS,
            errorOnNoContent: false
        }
    )

/**
 * Deletes a compliance policy asynchronously from a specific user.
 *
 * @param telsys - The ID of the telsys.
 * @param partition - The ID of the partition.
 * @param idOnTelSys - The ID of the user on the telsys.
 * @param policy - The policy assignment to be deleted.
 */
export const deleteCompliancePolicy = (
    telsys: string | number,
    partition: string | number,
    idOnTelSys: string,
    policy: PolicyAssignment[]
): Promise<Nullable<void>> =>
    requestJSONAsync<void>(
        getUserCompliancePolicyFromDeviceURL(telsys, partition, idOnTelSys),
        getTeamsAsyncResult(telsys),
        {
            method: RESTMethods.DELETE,
            body: policy,
            timingOptions: TIMING_OPTIONS,
            errorOnNoContent: false
        }
    )

/**
 * Fetches the tenant information asynchronously for the specified telsys.
 *
 * @param telsys - The ID of the telsys.
 */
export const getTenant = (telsys: string | number): Promise<Nullable<unknown>> =>
    requestJSONAsync(getTenantURL(telsys), getTeamsAsyncResult(telsys), {
        method: RESTMethods.GET,
        timingOptions: TIMING_OPTIONS
    })

/**
 * Deletes the tenant asynchronously for the specified telsys.
 *
 * @param telsys - The ID of the telsys.
 */
export const deleteTenant = (telsys: string | number): Promise<Nullable<unknown>> =>
    requestJSONAsync(getTenantURL(telsys), getTeamsAsyncResult(telsys), {
        method: RESTMethods.DELETE,
        timingOptions: TIMING_OPTIONS,
        errorOnNoContent: false
    })

/**
 * Fetches the tenant users asynchronously for the specified telsys.
 *
 * @param telsys - The ID of the telsys.
 */
export const getTenantUsers = (telsys: string | number): Promise<Nullable<unknown>> =>
    requestJSONAsync(getTenantUsersURL(telsys), getTeamsAsyncResult(telsys), {
        method: RESTMethods.GET,
        timingOptions: TIMING_OPTIONS
    })

/**
 * Fetches the voice applications asynchronously for the specified telsys.
 *
 * @param telsys - The ID of the telsys.
 */
export const getVoiceApps = (telsys: string | number): Promise<Nullable<void>> =>
    requestJSONAsync<void>(getVoiceAppsURL(telsys), getTeamsAsyncResult(telsys), {
        method: RESTMethods.GET,
        timingOptions: TIMING_OPTIONS
    })
